import { z } from 'zod';

import type { AlcoholCurvePoint } from '../alcohol';
import type {
  ConsumptionEvent,
  DeviceIdentity,
  DrinkEvent,
  Group,
  GroupMembership,
  GroupPhoto,
  PhotoLiker,
  PhotoLikeGroup,
  ActivityFeedEntry,
  InstallationIdentity,
  Participant,
} from './entities';

export const groupCodeSchema = z.string().regex(/^\d{6}$/);
export const timezoneSchema = z.string().min(1).max(64);
export const displayNameSchema = z.string().trim().min(1).max(30);
export const weightKgSchema = z.number().min(30).max(250).multipleOf(0.1);
export const heightCmSchema = z.number().min(120).max(230).multipleOf(0.1);
export const distributionFactorSchema = z.number().min(0.4).max(1);
export const eliminationRateSchema = z.number().min(0.05).max(0.3);
export const isoUtcSchema = z.iso.datetime({ offset: true });
export const localDateSchema = z.iso.date();
export const uuidSchema = z.uuid();

const participantProfileSchema = z.object({
  displayName: displayNameSchema,
  weightKg: weightKgSchema,
  distributionFactor: distributionFactorSchema.optional(),
  eliminationRate: eliminationRateSchema.optional(),
});

export const createGroupRequestSchema = z.object({
  name: z.string().trim().max(60).nullable().optional(),
  timezone: timezoneSchema,
  creator: participantProfileSchema,
});

export const lookupGroupRequestSchema = z.object({
  code: groupCodeSchema,
});

export const createParticipantRequestSchema = participantProfileSchema.extend({
  code: groupCodeSchema,
});

export const selectParticipantRequestSchema = z.object({
  groupId: uuidSchema,
  participantId: uuidSchema,
  code: groupCodeSchema,
});

export const createDrinkEventRequestSchema = z.object({
  id: uuidSchema,
  occurredAt: isoUtcSchema,
  volumeMl: z.number().int().min(10).max(5000),
  abv: z.number().min(0).max(100),
});

export const updateDrinkEventRequestSchema = z
  .object({
    occurredAt: isoUtcSchema.optional(),
    volumeMl: z.number().int().min(10).max(5000).optional(),
    abv: z.number().min(0).max(100).optional(),
  })
  .refine((input) => Object.values(input).some((value) => value !== undefined), {
    message: 'Mindestens ein Feld muss geändert werden.',
  });

export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;
export type LookupGroupRequest = z.infer<typeof lookupGroupRequestSchema>;
export type CreateParticipantRequest = z.infer<typeof createParticipantRequestSchema>;
export type SelectParticipantRequest = z.infer<typeof selectParticipantRequestSchema>;
export type CreateDrinkEventRequest = z.infer<typeof createDrinkEventRequestSchema>;
export type UpdateDrinkEventRequest = z.infer<typeof updateDrinkEventRequestSchema>;

const installationProfileSchema = z.object({
  weightKg: weightKgSchema,
  heightCm: heightCmSchema.nullable().optional(),
  distributionFactor: distributionFactorSchema.optional(),
  eliminationRate: eliminationRateSchema.optional(),
});

export const updateProfileRequestSchema = z
  .object({
    weightKg: weightKgSchema.optional(),
    heightCm: heightCmSchema.nullable().optional(),
    notificationsEnabled: z.boolean().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, {
    message: 'Mindestens eine Einstellung muss geändert werden.',
  });

export const pushSubscriptionRequestSchema = z.object({
  endpoint: z.url().max(2048),
  keys: z.object({
    p256dh: z.string().min(16).max(256),
    auth: z.string().min(8).max(128),
  }),
});
export const pushSubscriptionEndpointSchema = pushSubscriptionRequestSchema.pick({
  endpoint: true,
});

export const photoMimeTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const photoUploadIntentRequestSchema = z.object({
  drinkEventId: uuidSchema,
  byteSize: z.number().int().min(1).max(PHOTO_MAX_BYTES),
  width: z.number().int().min(1).max(10_000),
  height: z.number().int().min(1).max(10_000),
  mimeType: photoMimeTypeSchema,
});
export const photoDateSchema = z.iso.datetime({ offset: true });
export const photoLikeRequestSchema = z.object({
  groupId: uuidSchema,
});

/** Cloudflare Images binding `.input()` limit; not a product/UI limit. */
export const CLOUDFLARE_IMAGES_MAX_INPUT_BYTES = 20 * 1024 * 1024;
export const FEATURE_REQUEST_MAX_IMAGE_COUNT = 5;
export const featureRequestImageMimeTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/heic']);
export const createFeatureRequestSchema = z.object({
  id: uuidSchema,
  problem: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5_000),
});

export const migrateInstallationRequestSchema = installationProfileSchema.extend({
  /** Generated once locally before the first request; makes a lost response safe to retry. */
  installationId: uuidSchema,
  installationToken: z.string().length(64),
  additionalSessionTokens: z.array(z.string().length(64)).max(20).default([]),
});

export const createInstallationRequestSchema = installationProfileSchema.extend({
  /** Generated once locally before the first request; makes a lost response safe to retry. */
  installationId: uuidSchema,
  installationToken: z.string().length(64),
});

export const joinMembershipRequestSchema = z.object({
  sessionToken: z.string().length(64),
});

export const createConsumptionEventRequestSchema = createDrinkEventRequestSchema.extend({
  /**
   * The audience is captured by the PWA at tap time, so an offline leave/join
   * command cannot silently change where an already consumed drink appears.
   */
  audienceMembershipIds: z.array(uuidSchema).min(1).max(50),
});
export const updateConsumptionEventRequestSchema = updateDrinkEventRequestSchema;

export type MigrateInstallationRequest = z.infer<typeof migrateInstallationRequestSchema>;
export type CreateInstallationRequest = z.infer<typeof createInstallationRequestSchema>;
export type JoinMembershipRequest = z.infer<typeof joinMembershipRequestSchema>;
export type CreateConsumptionEventRequest = z.infer<typeof createConsumptionEventRequestSchema>;
export type UpdateConsumptionEventRequest = z.infer<typeof updateConsumptionEventRequestSchema>;
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;
export type PushSubscriptionRequest = z.infer<typeof pushSubscriptionRequestSchema>;
export type PhotoUploadIntentRequest = z.infer<typeof photoUploadIntentRequestSchema>;
export type PhotoLikeRequest = z.infer<typeof photoLikeRequestSchema>;
export type CreateFeatureRequest = z.infer<typeof createFeatureRequestSchema>;
export type FeatureRequestImageMimeType = z.infer<typeof featureRequestImageMimeTypeSchema>;

export interface PhotoUploadIntent {
  photoId: string;
  uploadUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
  maxBytes: number;
}

export interface PhotoUploadStatus {
  dailyLimit: number | null;
  dailyUsed: number;
  dailyRemaining: number | null;
  dailyUnlimited: boolean;
}

export interface PhotoLikeResult {
  liked: boolean;
  likeCount: number;
  likers: PhotoLiker[];
  outsideGroupLikeCount?: number;
  likeGroups?: PhotoLikeGroup[];
}

export interface PhotoStorageUsage {
  groupId: string;
  groupName: string | null;
  photoCount: number;
  groupStorageBytes: number;
  kvPhotoCount: number;
  kvStorageBytes: number;
  measuredAt: string;
  freeStorageBytes: number;
  maxPhotoBytes: number;
}

export interface PushTestResult {
  subscriptionCount: number;
  results: Array<{
    status: 'accepted' | 'failed';
    httpStatus: number | null;
    error?: string;
  }>;
}

export interface FeatureRequestResult {
  id: string;
  deliveredAt: string;
}

export interface ApiError {
  code: string;
  message: string;
  fieldErrors?: Record<string, string[]>;
}

export interface ApiMeta {
  requestId: string;
  nextCursor?: string;
}

export type ApiResponse<T> =
  | {
      data: T;
      error: null;
      meta: ApiMeta;
    }
  | {
      data: null;
      error: ApiError;
      meta: ApiMeta;
    };

export interface ParticipantAlcoholCurve {
  participantId: string;
  currentEstimate: number;
  points: AlcoholCurvePoint[];
  forecastPoints?: AlcoholCurvePoint[];
}

export interface GroupAlcoholSnapshot {
  calculatedAt: string;
  rangeStart: string;
  rangeEnd: string;
  curves: ParticipantAlcoholCurve[];
  /** Events loaded for this history window, including model context before it. */
  events?: DrinkEvent[];
}

export interface SessionBootstrap {
  group: Group;
  participant: Participant;
  participants: Participant[];
  drinkEvents: DrinkEvent[];
  alcoholSnapshot: GroupAlcoholSnapshot;
  weeklyAlcoholSnapshot: GroupAlcoholSnapshot;
  photos?: GroupPhoto[];
  identity: Pick<DeviceIdentity, 'groupId' | 'participantId' | 'role'>;
}

export interface GroupCreationResult {
  group: Group & { code: string };
  participant: Participant;
  identity: DeviceIdentity;
  invitation: GroupInvitation;
}

export interface GroupLookupResult {
  group: Group;
  participants: Participant[];
}

export interface ParticipantSessionResult {
  participant: Participant;
  identity: DeviceIdentity;
}

export interface GroupInvitation {
  code: string;
  expiresAt: string;
}

export interface InstallationBootstrap {
  installation: InstallationIdentity;
  memberships: GroupMembership[];
}

export interface InstallationMembershipBootstrap {
  profile: InstallationIdentity['profile'];
  memberships: GroupMembership[];
}

export interface ActivityFeedResult {
  entries: ActivityFeedEntry[];
  latestAt: string | null;
  hasNew: boolean;
}

export type ConsumptionMutationResult = ConsumptionEvent;

export interface DeleteResourceResult {
  id: string;
  deleted: true;
}
